/**
 * printer-adapters — Couche drivers/adapters d'impression VisionPOS.
 *
 * Un adapter = une méthode de transport physique vers l'imprimante.
 * Chaque adapter reçoit soit un Uint8Array ESC/POS soit des données
 * structurées et retourne Promise<boolean>.
 *
 * Adapters présents :
 *   - sendViaBluetooth()          — ESC/POS via BluetoothBridge Capacitor
 *   - sendViaAidlRaw()            — ESC/POS raw sur bridge AIDL Sunmi (sendRawData)
 *   - sendViaAidl()               — ticket caisse via AIDL natif (printText/setFontSize)
 *   - sendViaCloudPrinter()       — ticket caisse via Sunmi PrinterX (D3 Pro)
 *   - sendViaNetwork()            — ESC/POS raw via Edge Function print-document
 *
 * Rapports Z/X : voir printer-adapters-z.ts (extrait Phase 3c — split > 500 LOC).
 *
 * DETTE (Phase D) : les appels supabase.functions.invoke() de sendViaNetwork()
 * et le bridge window.SunmiPrinter* doivent migrer vers src/api/printing/.
 *
 * Invariant 4.A.16 : les mentions NF525 (DUPLICATA, SCEAU DE SECURITE,
 * mention_nf525) sont rendues via les mêmes conditions/chaînes que dans
 * buildEscPosReceipt — aucune divergence de format tolérée.
 */

import { supabase } from '@/integrations/supabase/client'; // DEBT(Phase-D): legacy — frontière publique = src/api/printing/ // eslint-disable-line
import { devLog } from '@/lib/logger';
import {
  AIDL_DEFAULT_SPACING,
  AIDL_FEED_BEFORE_CUT,
  AIDL_FONT_SIZE,
  AIDL_LINE_SPACING,
  columnsAt,
  formatLine,
  formatRow,
  type LineAlign,
  usesD3ReceiptLayout,
} from './aidl-receipt-layout';
import { sendViaAidlPreviousLayout } from './printer-adapters-aidl-previous';
import { globalDiscountRows, formatTicketAmount } from './ticket-discount';
import { ticketFiscalMention } from '@/config/editor';
import { wrapText, paymentRows } from './escpos-formatter';
import type { TicketData } from './escpos-types';
import { logPrintAttempt } from '@/api/printing/logging';

// Re-export Z adapters so that callers who import the full printer-adapters module
// continue to have access without changing their import paths.
export { sendViaAidlZReport, sendViaCloudPrinterZReport } from './printer-adapters-z';
export type { ZReportData } from './escpos-formatter';

// ── Bluetooth ────────────────────────────────────────────────────────────────
// Extrait dans bt-adapter.ts (cliquet max-lines) — ré-exporté pour les appelants.
export { sendViaBluetooth } from './bt-adapter';

// ── Network (Edge Function) ──────────────────────────────────────────────────

/**
 * Envoie un buffer ESC/POS via l'Edge Function print-document (TCP réseau).
 * DEBT(Phase-D) : migrer vers src/api/printing/ (invariant 4.B.2).
 */
export async function sendViaNetwork(ip: string, port: number, data: Uint8Array): Promise<boolean> {
  const { data: result, error } = await supabase.functions.invoke('print-document', {
    body: { raw_escpos: Array.from(data), ip_address: ip, port },
  });
  if (error) throw error;
  return result?.success === true;
}

// ── AIDL Sunmi ───────────────────────────────────────────────────────────────

/**
 * Envoie un buffer ESC/POS raw via AIDL bridge (sendRawData en base64).
 * Même rendu que BT — formatage identique.
 *
 * Retry borné 3 tentatives avec backoff 400 ms entre chaque (directive user 2026-06-12).
 * Timeout explicite 8 s par tentative.
 * Log systématique dans print_attempts_log (chemin/durée/erreur).
 */
export async function sendViaAidlRaw(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- bridge AIDL Sunmi non typé (objet Android exposé via WebView)
  bridge: any,
  data: Uint8Array,
): Promise<boolean> {
  const CONNECT_TIMEOUT_MS = 8_000;
  const MAX_RETRIES = 3;
  const RETRY_BACKOFF_MS = 400;

  // Attente de connexion (borné à CONNECT_TIMEOUT_MS total)
  const connectStart = Date.now();
  for (let i = 0; i < 5; i++) {
    if (Date.now() - connectStart >= CONNECT_TIMEOUT_MS) break;
    try {
      if (bridge.isConnected?.()) break;
    } catch {
      /* ignore */
    }
    try {
      bridge.init?.();
    } catch {
      /* ignore */
    }
    await new Promise((r) => setTimeout(r, 600 + i * 200));
  }

  let lastErr: unknown;
  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    const start = Date.now();
    try {
      if (typeof bridge.sendRawData === 'function') {
        const b64 = btoa(String.fromCharCode(...data));
        bridge.sendRawData(b64);
        const durationMs = Date.now() - start;
        devLog(
          '[PrintService] AIDL raw print OK:',
          data.length,
          'bytes, attempt',
          attempt,
          durationMs + 'ms',
        );
        logPrintAttempt({
          documentType: 'receipt',
          route: 'aidl',
          success: true,
          payloadSizeBytes: data.length,
          durationMs,
          metadata: { attempt },
        }).catch(() => {
          /* fire-and-forget */
        });
        return true;
      }
      // Fallback : écriture via printText ligne par ligne (moins fiable)
      const text = new TextDecoder().decode(data);
      bridge.beginTransaction();
      for (const line of text.split('\n')) {
        bridge.printText(line);
      }
      bridge.feedLines(3);
      bridge.commitTransaction();
      const durationMs = Date.now() - start;
      devLog(
        '[PrintService] AIDL raw fallback (printText) OK, attempt',
        attempt,
        durationMs + 'ms',
      );
      logPrintAttempt({
        documentType: 'receipt',
        route: 'aidl',
        success: true,
        payloadSizeBytes: data.length,
        durationMs,
        metadata: { attempt, path: 'printText_fallback' },
      }).catch(() => {
        /* fire-and-forget */
      });
      return true;
    } catch (err) {
      lastErr = err;
      const durationMs = Date.now() - start;
      const errMsg = err instanceof Error ? err.message : String(err);
      console.error(`[PrintService] AIDL raw error attempt ${attempt}/${MAX_RETRIES}:`, err);
      logPrintAttempt({
        documentType: 'receipt',
        route: 'aidl',
        success: false,
        payloadSizeBytes: data.length,
        durationMs,
        errorMessage: errMsg,
        metadata: { attempt },
      }).catch(() => {
        /* fire-and-forget */
      });
      if (attempt < MAX_RETRIES) {
        await new Promise((r) => setTimeout(r, RETRY_BACKOFF_MS * attempt));
      }
    }
  }
  console.error('[PrintService] AIDL raw: toutes les tentatives ont échoué', lastErr);
  return false;
}

/**
 * Imprime un ticket de caisse via les commandes AIDL natives Sunmi
 * (printText, setFontSize, printColumns…). Plus fiable que ESC/POS raw
 * pour l'encodage des accents et la gestion de la taille sur D3/D3Mini.
 *
 * INVARIANT 4.A.16 : les blocs DUPLICATA et SCEAU DE SECURITE doivent
 * rester byte-identiques à buildEscPosReceipt — toute modification ici
 * doit être portée en parallèle dans escpos-formatter.ts.
 *
 * Retry borné 3 tentatives avec backoff 400 ms entre chaque (directive user 2026-06-12).
 * Timeout explicite 8 s sur la phase de connexion.
 * Log systématique dans print_attempts_log (chemin/durée/erreur).
 */
export async function sendViaAidl(data: TicketData): Promise<boolean> {
  if (!usesD3ReceiptLayout()) return sendViaAidlPreviousLayout(data);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- bridge AIDL Sunmi non typé (objet Android exposé via WebView)
  const w = window as any;
  const b = w.SunmiPrinter || w.SunmiInnerPrinter;
  if (!b) return false;

  const CONNECT_TIMEOUT_MS = 8_000;
  const MAX_RETRIES = 3;
  const RETRY_BACKOFF_MS = 400;

  // Attente de connexion (borné à CONNECT_TIMEOUT_MS total)
  const connectStart = Date.now();
  for (let i = 0; i < 5; i++) {
    if (Date.now() - connectStart >= CONNECT_TIMEOUT_MS) break;
    try {
      if (b.isConnected?.()) break;
    } catch {
      /* ignore */
    }
    try {
      b.init?.();
    } catch {
      /* ignore */
    }
    await new Promise((r) => setTimeout(r, 600 + i * 200));
  }

  // Taille native, indépendante du réglage du ticket : c'est elle qui donne 48 colonnes en 80 mm.
  const paperMm = data.config.largeur_papier === 58 ? 58 : 80;
  const ps = AIDL_FONT_SIZE;
  const psHeader = ps + 3;
  const psTotal = ps + 3;
  const cpl = columnsAt(ps, paperMm);
  let fontSize = ps;
  const setSize = (size: number) => {
    fontSize = size;
    b.setFontSize(size);
  };
  const columns = () => columnsAt(fontSize, paperMm);
  const sendRaw = (bytes: number[]) => b.sendRawData?.(btoa(String.fromCharCode(...bytes)));
  const printLine = (text: string, align: LineAlign = 0) =>
    formatLine(text, align, columns()).forEach((line) => b.printText(line));
  const printRow = (left: string, right: string) =>
    formatRow(left, right, columns()).forEach((line) => b.printText(line));
  const printRule = (char: string) => b.printText(char.repeat(columns()));

  let lastAidlErr: unknown;
  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    const printStart = Date.now();
    try {
      b.beginTransaction();
      sendRaw(AIDL_LINE_SPACING);
      setSize(ps);

      // Bandeau DUPLICATA (NF525 art. 286 — copie identifiée comme telle)
      if (data.isDuplicate) {
        b.setBold(true);
        setSize(psHeader + 4);
        printLine('*** DUPLICATA ***', 1);
        setSize(ps);
        b.setBold(false);
        b.printText('\n');
      }

      // Header centré
      b.setBold(true);
      setSize(psHeader);
      printLine(data.store_name, 1);
      b.setBold(false);
      setSize(ps);
      if (data.store_subtitle) printLine(data.store_subtitle, 1);
      if (data.store_address) data.store_address.split('\n').forEach((l) => printLine(l, 1));
      if (data.store_phone) printLine('Tel : ' + data.store_phone, 1);
      if (data.store_email) printLine(data.store_email, 1);
      if (data.store_siret) printLine('SIRET : ' + data.store_siret, 1);
      if (data.tva_intracom) printLine('TVA Intra : ' + data.tva_intracom, 1);

      // Meta — centré
      setSize(ps);
      printRule('-');
      if (data.config.show_num_ticket && data.ticket_number) {
        b.setBold(true);
        setSize(ps + 2);
        printLine(data.ticket_number, 1);
        b.setBold(false);
      }
      setSize(ps);
      if (data.config.show_date && data.config.show_heure)
        printLine(data.date + ' ' + data.time, 1);
      else if (data.config.show_date) printLine(data.date, 1);
      else if (data.config.show_heure) printLine(data.time, 1);
      if (data.config.show_vendeur) printLine('Vendeur : ' + data.cashier, 1);
      printLine(data.register, 1);
      if (data.config.show_client && data.client) printLine('Client : ' + data.client, 1);

      // Items
      setSize(ps + 2);
      printRule('-');
      for (const item of data.items) {
        if (data.config.show_ref_article && item.ref) {
          setSize(ps - 2);
          printLine('  ' + item.ref, 0);
          setSize(ps + 2);
        }
        printRow(
          item.quantity + 'x ' + item.name,
          (item.quantity * item.unit_price).toFixed(2) + ' EUR',
        );
      }
      printRule('-');
      for (const row of globalDiscountRows(data))
        printRow(row.label, formatTicketAmount(row.amount));

      // TVA
      if (data.config.show_detail_tva) {
        setSize(ps);
        printRow('Sous-total HT', data.total_ht.toFixed(2) + ' EUR');
        if (data.config.show_tva_par_taux) {
          for (const tva of data.tva_breakdown) {
            printRow('TVA ' + tva.rate + '%', tva.tva.toFixed(2) + ' EUR');
          }
        }
      }

      // Total
      printRule('=');
      b.setBold(true);
      setSize(psTotal);
      printRow('TOTAL TTC', data.total_ttc.toFixed(2) + ' EUR');
      b.setBold(false);
      setSize(ps);
      printRule('=');

      // Payment — une ligne par moyen (fractionné) ou la ligne Reglement classique
      if (data.config.show_mode_reglement) {
        setSize(ps + 2);
        for (const row of paymentRows(data)) printRow(row.label, row.value);
        if (data.card_line) {
          // Réglage retour_tpe_detaille : une ligne « CB : VISA ****1234 · Aut. … · TID … »
          setSize(ps - 2);
          for (const l of wrapText(data.card_line, cpl - 2)) printLine('  ' + l, 0);
          setSize(ps);
        } else if (data.card_scheme) {
          setSize(ps - 2);
          // TPE bancaire externe : pas d'EMV myPOS, mention + ref optionnelle
          if (data.card_scheme === 'EXTERNAL_TPE') {
            const refSuffix = data.rrn ? ' - ref ' + data.rrn : '';
            printLine('  TPE externe' + refSuffix, 0);
          } else {
            printLine('  ' + data.card_scheme, 0);
            if (data.pan_masked) printLine('  PAN: ' + data.pan_masked, 0);
            if (data.auth_code) printLine('  Auth: ' + data.auth_code, 0);
            if (data.aid) printLine('  AID: ' + data.aid, 0);
            if (data.entry_mode) printLine('  Mode: ' + data.entry_mode, 0);
            if (data.tid) printLine('  TID: ' + data.tid, 0);
            if (data.mid) printLine('  MID: ' + data.mid, 0);
          }
          setSize(ps);
        }
      }

      // Footer — word-wrap pour que les mentions légales longues cassent sur les espaces
      setSize(ps);
      if (data.config.footer_legal)
        for (const l of wrapText(data.config.footer_legal, cpl)) printLine(l, 1);
      if (data.config.footer_text)
        for (const l of wrapText(data.config.footer_text, cpl)) printLine(l, 1);
      if (data.config.show_merci) {
        b.setBold(true);
        setSize(ps + 1);
        for (const l of wrapText(data.config.merci_text || 'Merci de votre visite !', cpl))
          printLine(l, 1);
        b.setBold(false);
      }
      if (data.config.mention_agec) {
        setSize(ps - 2);
        for (const l of wrapText(data.config.mention_agec, cpl)) printLine(l, 1);
      }
      setSize(ps - 2);
      for (const l of wrapText(ticketFiscalMention(data.config.mention_nf525), cpl))
        printLine(l, 1);

      // QR Code
      if (data.config.show_qr_code && typeof b.printQRCode === 'function') {
        b.setAlignment(1);
        const qrContent = data.config.qr_code_content || data.store_name;
        try {
          b.printQRCode({ data: qrContent, moduleSize: 6, errorLevel: 1 });
        } catch {
          /* ignore — certains firmwares ne supportent pas printQRCode */
        }
      }

      // Sceau de sécurité NF525
      if (data.config.show_hash_fiscal && data.fiscal_hash) {
        printRule('-');
        b.setBold(true);
        setSize(ps);
        printLine('SCEAU DE SECURITE', 1);
        b.setBold(false);
        setSize(ps);
        const h = data.fiscal_hash;
        for (let i = 0; i < h.length; i += cpl) {
          printLine(h.substring(i, Math.min(i + cpl, h.length)), 1);
        }
        if (data.ticket_number) printLine('Ticket: ' + data.ticket_number, 1);
      }

      // Le dernier texte doit passer la lame avant la coupe : interligne par défaut pour que
      // l'avance garde sa longueur. Pas d'avance après la coupe, le ticket suivant commencerait à blanc.
      sendRaw(AIDL_DEFAULT_SPACING);
      b.feedLines(AIDL_FEED_BEFORE_CUT);
      b.cutPaper?.();
      const durationMs = Date.now() - printStart;
      devLog('[PrintService] AIDL ticket OK attempt', attempt, durationMs + 'ms');
      logPrintAttempt({
        documentType: 'receipt',
        route: 'aidl',
        success: true,
        durationMs,
        metadata: { attempt },
      }).catch(() => {
        /* fire-and-forget */
      });
      return true;
    } catch (err) {
      lastAidlErr = err;
      const durationMs = Date.now() - printStart;
      const errMsg = err instanceof Error ? err.message : String(err);
      console.error(`[PrintService] AIDL error attempt ${attempt}/${MAX_RETRIES}:`, err);
      logPrintAttempt({
        documentType: 'receipt',
        route: 'aidl',
        success: false,
        durationMs,
        errorMessage: errMsg,
        metadata: { attempt },
      }).catch(() => {
        /* fire-and-forget */
      });
      if (attempt < MAX_RETRIES) {
        await new Promise((r) => setTimeout(r, RETRY_BACKOFF_MS * attempt));
      }
    }
  } // end retry loop
  console.error('[PrintService] AIDL: toutes les tentatives ont échoué', lastAidlErr);
  return false;
}

// ── Cloud Printer (Sunmi PrinterX) ───────────────────────────────────────────

/**
 * Imprime un ticket de caisse via le Sunmi Cloud Printer (D3 Pro, PrinterX SDK).
 * Sérialise le même TicketData que AIDL et ESC/POS pour un rendu uniforme.
 */
export async function sendViaCloudPrinter(data: TicketData): Promise<boolean> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- bridge PrinterX Sunmi non typé (objet Android exposé via WebView)
  const w = window as any;
  const px = w.SunmiPrinterX;
  if (!px?.printToCloudPrinter) {
    console.warn('[PrintService] SunmiPrinterX bridge not available');
    return false;
  }
  try {
    const payload = {
      tenant_name: data.store_name,
      tenant_address: data.store_address || '',
      tenant_phone: data.store_phone || '',
      tenant_email: data.store_email || '',
      tenant_siret: data.store_siret || '',
      tva_intracom: data.tva_intracom || '',
      store_subtitle: data.store_subtitle || '',
      register: data.register,
      ticket_number: data.config.show_num_ticket ? data.ticket_number || '' : '',
      date: data.date + (data.config.show_heure ? ' ' + data.time : ''),
      cashier: data.config.show_vendeur ? data.cashier : '',
      client: data.config.show_client ? data.client || '' : '',
      items: data.items
        .map((i) => ({
          name: i.name,
          qty: i.quantity,
          unit_price: i.unit_price,
          total: i.quantity * i.unit_price,
          tva_rate: i.tva_rate,
          ref: data.config.show_ref_article ? i.ref || '' : '',
        }))
        .concat(
          // Le rendu natif PrinterX ne connaît que les lignes : la remise globale y passe en ligne négative.
          globalDiscountRows(data)
            .filter((r) => r.amount < 0)
            .map((r) => ({
              name: r.label,
              qty: 1,
              unit_price: r.amount,
              total: r.amount,
              tva_rate: 0,
              ref: '',
            })),
        ),
      total_ht: data.total_ht,
      total_tva: data.tva_breakdown.reduce((s, t) => s + t.tva, 0),
      total_ttc: data.total_ttc,
      tva_breakdown: data.config.show_detail_tva ? data.tva_breakdown : [],
      payment_method: data.config.show_mode_reglement ? data.payment_method : '',
      card_scheme: data.card_scheme || '',
      pan_masked: data.pan_masked || '',
      auth_code: data.auth_code || '',
      aid: data.aid || '',
      entry_mode: data.entry_mode || '',
      tid: data.tid || '',
      mid: data.mid || '',
      fiscal_hash: data.config.show_hash_fiscal ? data.fiscal_hash || '' : '',
      footer_legal: data.config.footer_legal || '',
      footer_text: data.config.footer_text || '',
      merci_text: data.config.show_merci ? data.config.merci_text || 'Merci de votre visite !' : '',
      mention_nf525: ticketFiscalMention(data.config.mention_nf525),
      paper_width_mm: data.config.largeur_papier || 80,
    };
    return !!px.printToCloudPrinter(JSON.stringify(payload));
  } catch (err) {
    console.error('[PrintService] Cloud print error:', err);
    return false;
  }
}
